import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormCheck } from "react-bootstrap";
import { IListColor } from "../utils/models";
import { generateSecureRandomId } from "../utils/AppFunction";
import { formatDateToDDMMYYYY, formatMoney } from "../utils/FormatUtils";
import FormInPhieuDialog from "./button-in-phieu/components/PrintDialog";
import { DISPLAY_INVOICES } from "../utils/Constant";
import { einvoiceService } from "../einvoice/services/einvoiceService";
import { toast } from "react-toastify";
import { localStorageItem } from "../utils/LocalStorage";
import { INV_TYPE_CODE } from "../phan-he-vien-phi/const/VienPhiConstant";
import EinvoicePDFViewer from "../einvoice/components/EinvoicePDFViewer";
import DatePickerCustom from "./date-picker/DatePickerCustom";
import moment from "moment";
import { InvoiceListItem } from "../einvoice/types";
import "./style.scss";

// Default functions outside component to prevent re-creation
const defaultFetchExport = () => {};
const defaultHandleGetParams = () => {};

type Props = {
  dsPhieu: any[];
  listColorPhieu?: IListColor[];
  handleSelectRowData?: (rowData: any, index?: number) => void;
  handleContextMenu?: (e: any, item: any) => void;
  className?: string;
  title?: string;
  isGetFirstData?: boolean;
  selectedIndex?: number;
  isAllowPrint?: boolean;
  fetchExport?: (params: any) => any;
  handleGetParams?: (item: any) => any;
  subsystemCode?: string; // Thêm emrUrl để dùng làm subsystemCode
  enableEinvoiceCheckbox?: boolean; // Enable checkbox mode for e-invoice creation
  onEinvoiceCreated?: () => void; // Callback after e-invoice is created
  onSelectionChange?: (
    selectedInvoices: string[],
    eligibleCount: number,
    isCreating: boolean,
    handlers: {
      handleCreateBatch: () => void;
      handleSelectAll: () => void;
      handleClearSelection: () => void;
    }
  ) => void; // Expose selection state and handlers to parent
  onNavigateToEinvoice?: () => void; // Navigate to einvoice list
  enableDateEdit?: boolean; // Enable editing prescription date
  onDateUpdate?: (item: any, newDate: string) => Promise<void>; // Callback for updating date
  glEntriesButton?: React.ReactNode; // GL Entries button
  visitId?: string; // Visit ID for creating einvoices for the entire visit
  additionalActions?: {
    iconClassName: string;
    tooltip?: string;
    onClick: (item: any) => any;
  }[];
};

const DanhSachPhieu = ({
  dsPhieu,
  listColorPhieu,
  handleSelectRowData,
  handleContextMenu,
  className,
  title,
  isGetFirstData,
  selectedIndex,
  isAllowPrint,
  fetchExport = defaultFetchExport,
  handleGetParams = defaultHandleGetParams,
  subsystemCode = "CIS", // Default value nếu không truyền vào
  enableEinvoiceCheckbox = false,
  onEinvoiceCreated,
  onSelectionChange,
  onNavigateToEinvoice,
  enableDateEdit = false,
  onDateUpdate,
  glEntriesButton,
  visitId,
  additionalActions = [],
}: Props) => {
  const [currentIndex, setIndex] = useState<any>(null);
  const [danhSachPhieu, setDanhSachPhieu] = useState<any>([]);
  const [showAll, setShowAll] = useState(false);
  const [itemPrint, setItemPrint] = useState<any>(null);
  const [paramsPrint, setParamsPrint] = useState<any>(null);
  const [itemEinvoiceViewer, setItemEinvoiceViewer] = useState<any>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [isCreatingEinvoice, setIsCreatingEinvoice] = useState(false);
  const [editingId, setEditingId] = useState<any>(null);
  const [editingDate, setEditingDate] = useState<string>("");
  const [einvoices, setEinvoices] = useState<InvoiceListItem[]>([]);
  const [isLoadingEinvoices, setIsLoadingEinvoices] = useState(false);

  // Memoize onHide callback to prevent PrintDialog re-render
  const handleClosePrintDialog = useCallback(() => {
    console.log('📤 FormInPhieuDialog onHide called - closing modal');
    setItemPrint(null);
    setParamsPrint(null);
  }, []);

  const canCreateEinvoice = (item: any) => {
    return (
      !item?.einvoiceNumber &&
      !item?.invCanceled &&
      item?.invTypeCode === INV_TYPE_CODE.PHIEU_THU.code
    );
  };

  // Get eligible invoices for e-invoice creation
  const getEligibleInvoices = () => {
    return dsPhieu?.filter(canCreateEinvoice) || [];
  };

  // Toggle checkbox for a single invoice
  const handleToggleInvoice = (invCode: string, checked: boolean) => {
    setSelectedInvoices((prev) =>
      checked ? [...prev, invCode] : prev.filter((code) => code !== invCode)
    );
  };

  // Tạo hóa đơn điện tử cho visit
  const handleCreateEinvoiceForVisit = async () => {
    if (!visitId) {
      toast.error("Không tìm thấy visitId để tạo hóa đơn điện tử");
      return;
    }

    try {
      setIsCreatingEinvoice(true);
      const userInfo = localStorageItem.get("current-user") || {};
      const requestedBy =
        userInfo?.name ||
        userInfo?.sub ||
        userInfo?.username ||
        userInfo?.email ||
        "system";

      toast.info("Đang tạo hóa đơn điện tử cho bệnh nhân.");

      const response = await einvoiceService.createInvoicesFromVisitGL(visitId, {
        invoiceCodes: [], // Not used for GL-based method
        subsystemCode: subsystemCode,
        note: `Tạo hóa đơn điện tử GL-based cho visit ${visitId}`,
        requestedBy: requestedBy,
      });

      if (response.data?.invoiceNo) {
        // Success case: invoice created
        toast.success(
          `Đã tạo hóa đơn điện tử thành công! Số HĐ: ${response.data.invoiceNo}`
        );
        onEinvoiceCreated?.(); // Refresh data
        fetchEinvoicesForVisit(); // Refresh e-invoice list
      } else {
        // Handle response with message from backend
        const message = response.message || "Lỗi không xác định";

        // Backend returns SUCCESS status for NO_ORDERS_TO_INVOICE case
        // so it will be handled as success by axios
        toast.info(message);
        onEinvoiceCreated?.(); // Refresh data
        fetchEinvoicesForVisit();
      }
    } catch (error: any) {
      console.error("Error creating e-invoices for visit:", error);
      let errorMessage = "Có lỗi xảy ra khi tạo hóa đơn điện tử";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;

        // Trường hợp đặc biệt: "Đang tạo hóa đơn..." không phải là lỗi
        if (errorMessage === "Đang tạo hóa đơn...") {
          toast.info("Hệ thống đang xử lý tạo hóa đơn điện tử. Vui lòng chờ trong giây lát...");
          onEinvoiceCreated?.(); // Refresh data to show processing status
          return; // Không hiển thị toast error
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsCreatingEinvoice(false);
    }
  };

  // Fetch e-invoices for visit
  const fetchEinvoicesForVisit = async () => {
    if (!visitId) {
      setEinvoices([]);
      return;
    }

    try {
      setIsLoadingEinvoices(true);
      const response = await einvoiceService.getInvoiceList({
        visitId: visitId,
        page: 1,
        size: 100,
      });

      console.log("📋 E-invoices response:", response);

      // API returns: {code: 200, data: {content: [...], pageable: {...}}}
      if (response.code === 200 && response.data?.content) {
        setEinvoices(response.data.content);
        console.log("✅ Loaded e-invoices:", response.data.content.length);
      } else {
        setEinvoices([]);
      }
    } catch (error) {
      console.error("Error fetching e-invoices for visit:", error);
      setEinvoices([]);
    } finally {
      setIsLoadingEinvoices(false);
    }
  };

  // Fetch e-invoices when visitId changes
  useEffect(() => {
    fetchEinvoicesForVisit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId]);

  // Select all eligible invoices (hoặc tạo hóa đơn cho visit)
  const handleSelectAll = async () => {
    console.log("🔍 handleSelectAll called - visitId:", visitId);

    // Nếu có visitId, tạo hóa đơn điện tử cho cả visit
    if (visitId) {
      console.log("✅ visitId exists, calling GL-based API");
      await handleCreateEinvoiceForVisit();
      return;
    }

    // Nếu không có visitId, chỉ select các invoices như cũ
    console.log("⚠️ No visitId, only selecting checkboxes");
    const eligibleInvoices = getEligibleInvoices();
    const allCodes = eligibleInvoices.map(
      (inv) => inv?.invCode || inv?.maPhieu
    );
    setSelectedInvoices(allCodes);
  };

  // Create e-invoices for selected invoices (or specified invoices)
  const handleCreateBatchEinvoices = async (invoiceCodesToCreate?: string[]) => {
    const invoiceCodes = invoiceCodesToCreate || selectedInvoices;

    if (invoiceCodes.length === 0) {
      toast.warning(
        "Vui lòng chọn ít nhất một phiếu thu để tạo hóa đơn điện tử"
      );
      return;
    }

    try {
      setIsCreatingEinvoice(true);
      const userInfo = localStorageItem.get("current-user") || {};
      const requestedBy =
        userInfo?.name ||
        userInfo?.sub ||
        userInfo?.username ||
        userInfo?.email ||
        "system";

      toast.info(`Đang tạo ${invoiceCodes.length} hóa đơn điện tử...`);

      const response = await einvoiceService.createInvoicesFromSubsystem({
        invoiceCodes: invoiceCodes,
        subsystemCode: subsystemCode,
        note: `Tạo hóa đơn điện tử từ ${invoiceCodes.length} phiếu thu`,
        requestedBy: requestedBy,
        visitId: visitId, // Truyền visitId để backend lưu vào metadata
      });

      if (response.data?.success) {
        toast.success(
          ` Đã gửi tạo ${response.data.successCount}/${response.data.totalRequested} hóa đơn điện tử thành công!`
        );

        // Update einvoiceNumber for successfully created invoices
        if (response.data.results) {
          setDanhSachPhieu((prevPhieu: any[]) =>
            prevPhieu.map((item: any) => {
              const invCode = item?.invCode || item?.maPhieu;
              const result = response.data.results?.[invCode];
              if (result && result.status === 'SUCCESS' && result.invoiceNumber) {
                return { ...item, einvoiceNumber: result.invoiceNumber };
              }
              return item;
            })
          );
        }

        setSelectedInvoices([]); // Clear selection after success
        onEinvoiceCreated?.(); // Trigger callback to refresh data
      } else {
        toast.success(
          `Đang tạo hóa đơn: ${
            response.data?.message || "Lỗi không xác định"
          }`
        );
      }
    } catch (error: any) {
      console.error("❌ Error creating batch e-invoices:", error);

      let errorMessage = "Có lỗi xảy ra khi tạo hóa đơn điện tử";
      if (error.response) {
        // Ưu tiên lấy message từ backend trước
        errorMessage = error.response?.data?.message || error.response?.data?.data?.message || errorMessage;

        // Chỉ dùng message hardcoded nếu backend không trả về message
        if (!errorMessage || errorMessage === "Có lỗi xảy ra khi tạo hóa đơn điện tử") {
          switch (error.response.status) {
            case 400:
              errorMessage = "Dữ liệu không hợp lệ. Vui lòng kiểm tra lại thông tin phiếu.";
              break;
            case 401:
              errorMessage = "Không có quyền thực hiện thao tác này.";
              break;
            case 500:
              errorMessage = "Lỗi hệ thống. Vui lòng thử lại sau.";
              break;
          }
        }
      } else if (error.request) {
        errorMessage =
          "Không thể kết nối đến server. Vui lòng kiểm tra kết nối mạng.";
      }
      toast.error(errorMessage);
    } finally {
      setIsCreatingEinvoice(false);
    }
  };

  // Hàm tạo hóa đơn điện tử từ phân hệ (single invoice)
  const handleCreateEinvoice = async (item: any) => {
    try {
      console.log("🏥 Creating e-invoice for item:", item);

      // Extract invoice code từ item (có thể là item.code, item.id, item.invCode, etc.)
      const invoiceCode = item?.invCode || item?.maPhieu || "";

      if (!invoiceCode) {
        toast.error("Không tìm thấy mã phiếu để tạo hóa đơn điện tử");
        return;
      }

      // Lấy thông tin user từ localStorage
      const userInfo = localStorageItem.get("current-user") || {};
      const requestedBy =
        userInfo?.name ||
        userInfo?.sub ||
        userInfo?.username ||
        userInfo?.email ||
        "system";
      toast.info("Đang tạo hóa đơn điện tử...");
      const response = await einvoiceService.createInvoicesFromSubsystem({
        invoiceCodes: [invoiceCode], // Truyền array với 1 invoice code
        subsystemCode: subsystemCode, // Sử dụng emrUrl làm subsystem code
        note: `Tạo hóa đơn điện tử từ phiếu ${invoiceCode}`,
        requestedBy: requestedBy, // Lấy từ localStorage
        visitId: visitId, // Truyền visitId để backend lưu vào metadata
      });
      if (response.data?.success) {
        toast.success(
          ` Đã gửi tạo hóa đơn điện tử thành công! (${response.data.successCount}/${response.data.totalRequested})`
        );
        // Có thể refresh danh sách hoặc update UI tại đây nếu cần
      } else {
        toast.success(
          `Đang tạo hóa đơn:  ${
            response.data?.message || "Lỗi không xác định"
          }`
        );
      }
    } catch (error: any) {
      console.error("❌ Error creating e-invoice:", error);

      let errorMessage = "Có lỗi xảy ra khi tạo hóa đơn điện tử";

      if (error.response) {
        // Ưu tiên lấy message từ backend trước
        errorMessage = error.response?.data?.message || error.response?.data?.data?.message || errorMessage;

        // Chỉ dùng message hardcoded nếu backend không trả về message
        if (!errorMessage || errorMessage === "Có lỗi xảy ra khi tạo hóa đơn điện tử") {
          switch (error.response.status) {
            case 400:
              errorMessage = "Dữ liệu không hợp lệ. Vui lòng kiểm tra lại thông tin phiếu.";
              break;
            case 401:
              errorMessage = "Không có quyền thực hiện thao tác này.";
              break;
            case 500:
              errorMessage = "Lỗi hệ thống. Vui lòng thử lại sau.";
              break;
          }
        }
      } else if (error.request) {
        errorMessage =
          "Không thể kết nối đến server. Vui lòng kiểm tra kết nối mạng.";
      }

      toast.error(errorMessage);
    }
  };

  const handleDoubleClickDate = (e: React.MouseEvent, item: any) => {
    if (!enableDateEdit) return;
    // Không cho phép sửa phiếu tạm ứng
    if (item.invTypeCode === INV_TYPE_CODE.PHIEU_TAM_UNG.code) {
      toast.warning("Không thể sửa thời gian phiếu tạm ứng");
      return;
    }
    e.stopPropagation();
    setEditingId(item.id || item.maPhieu);
    setEditingDate(item.prescriptionDate || item.date || item.invDate || "");
  };

  const handleSaveDate = async (item: any) => {
    if (!editingDate || !onDateUpdate) return;

    // Validate không trùng thời gian đến phút với phiếu khác
    const editingDateTime = moment(editingDate);
    const currentItemId = item.id || item.maPhieu;

    const duplicateSheet = dsPhieu?.find((sheet: any) => {
      const sheetId = sheet.id || sheet.maPhieu;
      if (sheetId === currentItemId) return false; // Skip current item

      const sheetDate = moment(
        sheet.prescriptionDate || sheet.date || sheet.invDate
      );
      return editingDateTime.isSame(sheetDate, "minute");
    });

    if (duplicateSheet) {
      toast.error(
        `Thời gian phiếu đã trùng với phiếu khác (${moment(
          duplicateSheet.prescriptionDate ||
            duplicateSheet.date ||
            duplicateSheet.invDate
        ).format("HH:mm DD/MM/YYYY")})`
      );
      return;
    }

    try {
      // Format datetime to ISO-8601 without timezone (yyyy-MM-ddTHH:mm:ss)
      // Backend expects format like "2025-10-22T08:00:00" without milliseconds and timezone
      const formattedDate = editingDateTime.format("YYYY-MM-DDTHH:mm:ss");
      await onDateUpdate(item, formattedDate);
      setEditingId(null);
      setEditingDate("");
      toast.success("Cập nhật thời gian thành công");
    } catch (error) {
      toast.error("Không thể cập nhật thời gian");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingDate("");
  };

  useEffect(() => {
    if (isGetFirstData) {
      setIndex(0);
      if (dsPhieu?.length) handleSelectRowData?.(dsPhieu[0]);
    } else {
      setIndex(selectedIndex ?? null);
    }

    // Clear checkbox selection when patient changes (dsPhieu changes)
    if (enableEinvoiceCheckbox) {
      setSelectedInvoices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsPhieu, selectedIndex]);

  useEffect(() => {
    setDanhSachPhieu(!showAll ? dsPhieu?.slice(0, 5) : dsPhieu);
  }, [showAll, dsPhieu]);

  // Notify parent about selection changes
  useEffect(() => {
    if (enableEinvoiceCheckbox && onSelectionChange) {
      // Count eligible invoices from ALL invoices (not just visible)
      const allEligibleInvoices = getEligibleInvoices();
      const eligibleCount = allEligibleInvoices.length;

      onSelectionChange(selectedInvoices, eligibleCount, isCreatingEinvoice, {
        handleCreateBatch: () => handleCreateBatchEinvoices(),
        handleSelectAll: handleSelectAll, // Use the new handleSelectAll that checks visitId
        handleClearSelection: () => setSelectedInvoices([]),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoices, dsPhieu, isCreatingEinvoice, enableEinvoiceCheckbox]);

  const handleClickPhieu = (currentIndex: number, item: any) => {
    setIndex(currentIndex);
    handleSelectRowData?.(item, currentIndex);
  };

  const renderColorTrangThaiPhieu = (trangThai: number) => {
    return (
      listColorPhieu?.find((item) => item?.code === trangThai) ?? { name: "" }
    );
  };

  const handleShowAllPhieu = () => {
    setShowAll(!showAll);
  };

  const handleCustomContextMenu = (e: any, item: any) => {
    e.preventDefault();
    handleContextMenu && handleContextMenu(e, item);
  };

  const renderEinvoices = () => {
    if (!visitId || einvoices.length === 0) {
      return null;
    }

    return (
      <div className="d-flex gap-2 ps-1">
        {einvoices.map((einvoice, index) => {
          // Tính tổng tiền từ products array
          const totalAmount = einvoice.products?.reduce((sum, product) => {
            return sum + (product.lineTotal || 0);
          }, 0) || 0;

          // Determine status class for border color
          let statusClass = "";
          if (einvoice.integrationStatus === "SUCCESS") {
            statusClass = "einvoice-success";
          } else if (einvoice.integrationStatus === "RETRYING") {
            statusClass = "einvoice-retrying";
          } else if (einvoice.integrationStatus === "FAILED") {
            statusClass = "einvoice-failed";
          }

          return (
            <div
              key={einvoice.ikey || index}
              className="cursor-pointer"
              onClick={() => setItemEinvoiceViewer({ einvoiceNumber: einvoice.ikey })}
              title={einvoice.createdDate ? `Thời gian tạo: ${moment(einvoice.createdDate).format("HH:mm DD/MM/YYYY")}` : ""}
            >
              <div
                className={`border spaces min-h-29 rounded-1 d-flex flex-column justify-content-center align-items-center phieu py-4 px-8 ${statusClass}`}
                style={{ position: "relative" }}
              >
                {/* Dòng 1: Số hóa đơn + icon retry */}
                <div className="text fw-semibold d-flex gap-2 align-items-center">
                  <span>{einvoice.invoiceNo || einvoice.invoiceNumber || "Chưa có số"}</span>
                  {/* Only show retry button for FAILED status (not RETRYING - let scheduler handle) */}
                  {einvoice.integrationStatus === "FAILED" && (
                    <i
                      className="bi bi-arrow-clockwise text fw-semibold"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const response = await einvoiceService.retryInvoiceFromIkey(einvoice.ikey);
                          if (response.message) {
                            toast.info(response.message);
                          } else {
                            toast.info("Đang thử lại tạo hóa đơn...");
                          }
                          // Fetch immediately to show updated status (RETRYING)
                          await fetchEinvoicesForVisit();
                        } catch (error: any) {
                          console.error("Error retrying invoice:", error);
                          toast.error(error.response?.data?.message || "Có lỗi khi thử lại");
                        }
                      }}
                      style={{ cursor: "pointer" }}
                      title="Thử lại"
                    />
                  )}
                </div>

                {/* Dòng 2: Tổng tiền */}
                <div className="text">
                  {formatMoney(totalAmount)} VNĐ
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPhieu = () => {
    return danhSachPhieu?.length > 0 ? (
      danhSachPhieu?.map((item: any, index: number) => {
        const invCode = item?.invCode || item?.maPhieu || "";
        const isEligibleForEinvoice = canCreateEinvoice(item);
        const isChecked = selectedInvoices.includes(invCode);

        return (
          <div
            key={generateSecureRandomId()}
            className={`cursor-pointer`}
            onClick={() => handleClickPhieu(index, item)}
            onContextMenu={(e) => handleCustomContextMenu(e, item)}
          >
            <div
              className={`border spaces  min-h-29 rounded-1 d-flex flex-column justify-content-center align-items-center phieu py-4 px-8 ${
                index === currentIndex
                  ? item?.invCanceled || item?.display === DISPLAY_INVOICES.HUY
                    ? "is-active-cancel"
                    : "is-active"
                  : ""
              }`}
              style={{ position: "relative" }}
            >
              {/* Show checkbox for manual selection (invoice-code based method) */}
              {enableEinvoiceCheckbox && isEligibleForEinvoice && (
                <div className="invoice-checkbox-wrapper">
                  <FormCheck
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleInvoice(invCode, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    label=""
                    className="m-0 p-0"
                    title="Chọn phiếu để tạo hóa đơn thủ công"
                  />
                </div>
              )}
              <div
                className={`${
                  item?.display === DISPLAY_INVOICES.HUY
                    ? "text-danger"
                    : "text"
                } fw-semibold d-flex gap-2 align-items-center`}
              >
                {editingId === (item.id || item.maPhieu) ? (
                  <div
                    className="d-flex align-items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DatePickerCustom
                      name="prescriptionDate"
                      value={editingDate}
                      setDateValue={(date) => setEditingDate(date || "")}
                      mode="date-time"
                      dateFormat="HH:mm dd/MM/yyyy"
                      placeholderText="14:02 07/10/2025"
                      inputClassName="form-control-sm"
                      showTimeSelect
                      timeIntervals={1}
                    />
                    <button
                      className="btn btn-sm btn-success d-flex align-items-center gap-1 px-2 py-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveDate(item);
                      }}
                      title="Lưu"
                    >
                      <i className="bi bi-check-circle-fill" />
                    </button>
                    <button
                      className="btn btn-sm btn-danger d-flex align-items-center gap-1 px-2 py-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelEdit();
                      }}
                      title="Hủy"
                    >
                      <i className="bi bi-x-circle-fill" />
                    </button>
                  </div>
                ) : (
                  <>
                    <span
                      onDoubleClick={(e) => handleDoubleClickDate(e, item)}
                      style={{
                        cursor:
                          enableDateEdit &&
                          item.invTypeCode !== INV_TYPE_CODE.PHIEU_TAM_UNG.code
                            ? "pointer"
                            : "default",
                      }}
                    >
                      {item?.date || formatDateToDDMMYYYY(item?.invDate) || ""}
                    </span>
                    {enableDateEdit &&
                      item.invTypeCode !== INV_TYPE_CODE.PHIEU_TAM_UNG.code && (
                        <i
                          className="bi bi-pencil-square text-primary"
                          style={{
                            fontSize: "0.9rem",
                            cursor: "pointer",
                            opacity: 0.7,
                          }}
                          onClick={(e) => handleDoubleClickDate(e, item)}
                          title="Sửa thời gian"
                        />
                      )}
                  </>
                )}
                {isAllowPrint && !item?.invCanceled && (
                  <i
                    className={`spaces ${
                      item?.display === DISPLAY_INVOICES.HUY
                        ? "text-danger"
                        : "text"
                    } fw-semibold bi bi-printer fs-3 m-0 p-0`}
                    onClick={(e) => {
                      e.stopPropagation(); // Ngăn event bubbling lên parent
                      setItemPrint(item);
                      setParamsPrint(handleGetParams(item));
                    }}
                    style={{ cursor: "pointer" }} // Thêm pointer cursor
                  ></i>
                )}
                {canCreateEinvoice(item) && (
                  <i
                    className={`spaces ${
                      item?.display === DISPLAY_INVOICES.HUY
                        ? "text-danger"
                        : "text"
                    } fw-semibold bi bi-file-earmark-plus fs-3 m-0 p-0`}
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Tạo hóa đơn điện tử"
                    onClick={(e) => {
                      e.stopPropagation(); // Ngăn event bubbling lên parent
                      handleCreateEinvoice(item);
                    }}
                    style={{ cursor: "pointer" }} // Thêm pointer cursor
                  ></i>
                )}
                {item?.einvoiceNumber && (
                  <i
                    className={`spaces ${
                      item?.display === DISPLAY_INVOICES.HUY
                        ? "text-danger"
                        : "text-primary"
                    } spaces text fw-semibold fs-3 m-0 p-0 bi bi-file-pdf`}
                    style={{ cursor: "pointer" }}
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Xem hóa đơn điện tử PDF"
                    onClick={(e) => {
                      e.stopPropagation(); // Ngăn event bubbling lên parent
                      setItemEinvoiceViewer(item);
                    }}
                  ></i>
                )}
                {item?.invCanceled && (
                  <i
                    className={`spaces ${
                      item?.display === DISPLAY_INVOICES.HUY
                        ? "text-danger"
                        : "text"
                    } fw-semibold bi bi-receipt-cutoff fs-3 m-0 p-0`}
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Hủy hóa đơn điện tử"
                    onClick={(e) => {
                      e.stopPropagation(); // Ngăn event bubbling lên parent
                      setItemPrint(item);
                      setParamsPrint(handleGetParams(item));
                    }}
                    style={{ cursor: "pointer" }} // Thêm pointer cursor
                  ></i>
                )}
                {additionalActions?.map((action, actionIndex) => (
                  <i
                    key={actionIndex}
                    className={`spaces fw-semibold m-0 p-0 fs-13px ${action?.iconClassName} ${index === currentIndex ? "text-white" : "text-pri"}`}
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={action?.tooltip || ""}
                    onClick={(e) => {
                      e.stopPropagation(); // Ngăn event bubbling lên parent
                      action.onClick(item);
                    }}
                    style={{ cursor: "pointer" }} // Thêm pointer cursor
                  ></i>
                ))}
              </div>

              {title && (
                <div
                  className={`${
                    item?.display === DISPLAY_INVOICES.HUY
                      ? "text-danger"
                      : "text"
                  }`}
                >
                  {item?.[title] || ""}
                </div>
              )}
            </div>
            <div className="d-flex spaces mt-1 gap-1">
              {item?.nhomDichVu?.map((item: any, index: number) => {
                return (
                  <div
                    key={index}
                    className={`spaces width-33 h-3 status-column ${
                      renderColorTrangThaiPhieu(item?.trangThai)?.name
                    } ${className}`}
                  ></div>
                );
              })}
            </div>
          </div>
        );
      })
    ) : (
      <div className="spaces min-h-32"></div>
    );
  };

  return (
    <>
      <div className="d-flex w-100 gap-2">
        {/* 1. Danh sách phiếu thu */}
        <div className={`d-flex gap-2 ps-1 ds-phieu pe-2 ${className}`}>
          {renderPhieu()}
          {danhSachPhieu?.length >= 5 && (
            <span
              className="d-flex align-items-center cursor-pointer mx-1"
              onClick={handleShowAllPhieu}
            >
              <i
                className={`fa-solid fa-circle-${
                  showAll ? "left" : "right"
                } fs-2`}
              ></i>
            </span>
          )}
        </div>

        {/* 2. Danh sách hóa đơn điện tử */}
        {renderEinvoices()}

        {/* 3. Các nút thao tác - luôn ở cuối bên phải */}
        <div className="d-flex gap-2 align-items-start ms-auto">
          {/* Button to navigate to einvoice list - icon only, square shape, right aligned */}
          {onNavigateToEinvoice && (
            <button
              type="button"
              className="btn btn-warning d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px", padding: 0, borderRadius: "4px" }}
              onClick={onNavigateToEinvoice}
              title="Danh sách HĐĐT"
            >
              <i style={{ padding: 0 }} className="bi bi-file-earmark-text fs-4"></i>
            </button>
          )}
          {/* GL Entries button next to HĐĐT button */}
          {glEntriesButton}
        </div>
      </div>

      <FormInPhieuDialog<any>
        key="print-dialog-stable"
        show={!!(itemPrint && paramsPrint)}
        onHide={handleClosePrintDialog}
        fetchExport={fetchExport}
        params={paramsPrint}
      />
      {itemEinvoiceViewer && (
        <EinvoicePDFViewer
          invoiceId={itemEinvoiceViewer?.einvoiceNumber}
          className="btn p-0 border-0 bg-transparent"
          title="Xem hóa đơn điện tử PDF"
          autoOpen={true}
          onViewPDF={() => {
            console.log("📄 Viewing PDF for invoice:", itemEinvoiceViewer);
          }}
          onHide={() => {
            setItemEinvoiceViewer(null); // Clear khi đóng dialog
          }}
        />
      )}
    </>
  );
};

export default DanhSachPhieu;
